import React from 'react';
import { Head, Link, useForm } from '@inertiajs/react';
import { Building2, PlusCircle, Upload } from 'lucide-react';
import { PlatformConsoleBreadcrumb } from '@/components/platform_console/PlatformConsoleBreadcrumb';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface Municipality {
  id: number;
  name: string;
  classification: string;
}

type MunicipalityMode = 'existing' | 'new';

interface DemoDocumentNewProps {
  municipalities: Municipality[];
  errors: Record<string, string[] | string>;
  municipality_mode: string;
  municipality_id: string;
  new_municipality_name: string;
  new_municipality_state: string;
}

function municipalityModeFromProp(value: string | undefined): MunicipalityMode {
  return value === 'new' ? 'new' : 'existing';
}

export default function DemoDocumentNew({
  municipalities,
  errors,
  municipality_mode,
  municipality_id,
  new_municipality_name,
  new_municipality_state,
}: DemoDocumentNewProps) {
  const { data, setData, errors: formErrors } = useForm({
    name: '',
    description: '',
    files: [] as File[],
    document_names: [] as string[],
    municipality_mode: municipalityModeFromProp(municipality_mode),
    municipality_id,
    new_municipality_name,
    new_municipality_state,
  });

  const allErrors = { ...errors, ...formErrors };
  const municipalityMode = municipalityModeFromProp(data.municipality_mode);
  const csrfToken = typeof document === 'undefined'
    ? ''
    : document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || '';

  const fileBaseName = (file: File) => file.name.replace(/\.[^/.]+$/, '');

  const updateSelectedFiles = (fileList: FileList | null) => {
    const files = Array.from(fileList || []);
    setData((currentData) => ({
      ...currentData,
      files,
      document_names: files.map((file, index) => currentData.document_names[index] || fileBaseName(file)),
    }));
  };

  const updateDocumentName = (index: number, value: string) => {
    const documentNames = [...data.document_names];
    documentNames[index] = value;
    setData('document_names', documentNames);
  };

  const renderError = (key: string) => {
    const value = allErrors[key as keyof typeof allErrors];
    if (!value) return null;

    return (
      <p className="text-sm text-red-600 mt-1">
        {Array.isArray(value) ? value.join(', ') : value}
      </p>
    );
  };

  return (
    <>
      <Head title="New Demo Document" />
      <div className="container mx-auto p-6 max-w-3xl">
        <div className="space-y-6">
          <PlatformConsoleBreadcrumb section="document_pipeline" current="Demo Documents" />
          <h1 className="text-3xl font-bold">Upload Demo Document</h1>

          <Alert>
            <AlertDescription>
              Upload one or more PDFs. Cadence is automatically set to yearly. New municipalities are assigned to the
              configured demo team.
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle>Demo Document Details</CardTitle>
            </CardHeader>
            <CardContent>
              <form className="space-y-6" action="/demo-documents" method="post" encType="multipart/form-data">
                <input type="hidden" name="authenticity_token" value={csrfToken} />
                <input type="hidden" name="municipality_mode" value={data.municipality_mode} />
                <input type="hidden" name="municipality_id" value={data.municipality_id} />
                <div className="space-y-2">
                  <Label htmlFor="name">Default Document Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={data.name}
                    onChange={(e) => setData('name', e.target.value)}
                    required={data.files.length <= 1}
                  />
                  <p className="text-sm text-gray-600">
                    Used when uploading one PDF, or as a fallback if a selected file name is left blank.
                  </p>
                  {renderError('name')}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={data.description}
                    onChange={(e) => setData('description', e.target.value)}
                    rows={4}
                  />
                  {renderError('description')}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="files">PDF Files</Label>
                  <Input
                    id="files"
                    name="files[]"
                    type="file"
                    accept="application/pdf"
                    multiple
                    onChange={(e) => updateSelectedFiles(e.target.files)}
                    required
                  />
                  {data.files.length > 1 ? (
                    <p className="text-sm text-gray-600">
                      Each selected PDF will be uploaded as a separate demo document with the name below.
                    </p>
                  ) : null}
                  {renderError('file')}
                  {renderError('files')}
                </div>

                {data.files.length > 0 ? (
                  <div className="space-y-3 rounded-lg border bg-gray-50 p-4">
                    <div>
                      <Label>Document Names</Label>
                      <p className="text-sm text-gray-600">
                        Rename each uploaded PDF before sending it through the demo flow.
                      </p>
                    </div>
                    <div className="space-y-3">
                      {data.files.map((file, index) => (
                        <div key={`${file.name}-${index}`} className="space-y-1">
                          <Label htmlFor={`document_name_${index}`} className="text-sm text-gray-700">
                            {file.name}
                          </Label>
                          <Input
                            id={`document_name_${index}`}
                            name="document_names[]"
                            value={data.document_names[index] || ''}
                            onChange={(e) => updateDocumentName(index, e.target.value)}
                            placeholder={fileBaseName(file)}
                            required
                          />
                          {renderError(`document_names.${index}`)}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="space-y-3">
                  <Label>Municipality</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => setData('municipality_mode', 'existing')}
                      className={`text-left rounded-lg border p-4 transition ${
                        municipalityMode === 'existing'
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2 font-semibold">
                        <Building2 className="h-4 w-4" />
                        Use Existing
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Pick a municipality that already exists in Ordinal.
                      </p>
                    </button>

                    <button
                      type="button"
                      onClick={() => setData('municipality_mode', 'new')}
                      className={`text-left rounded-lg border p-4 transition ${
                        municipalityMode === 'new'
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2 font-semibold">
                        <PlusCircle className="h-4 w-4" />
                        Create New
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Create a municipality and assign it to the configured demo team.
                      </p>
                    </button>
                  </div>
                </div>

                {municipalityMode === 'existing' ? (
                  <div className="space-y-2 rounded-lg border bg-gray-50 p-4">
                    <Label htmlFor="municipality_id">Existing Municipality</Label>
                    <Select
                      value={data.municipality_id}
                      onValueChange={(value) => setData('municipality_id', value)}
                    >
                      <SelectTrigger id="municipality_id">
                        <SelectValue placeholder="Select municipality" />
                      </SelectTrigger>
                      <SelectContent>
                        {municipalities.map((municipality) => (
                          <SelectItem key={municipality.id} value={String(municipality.id)}>
                            {municipality.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {renderError('municipality_id')}
                  </div>
                ) : (
                  <div className="space-y-4 rounded-lg border bg-gray-50 p-4">
                    <div className="space-y-2">
                      <Label htmlFor="new_municipality_name">New Municipality Name</Label>
                      <Input
                        id="new_municipality_name"
                        name="new_municipality_name"
                        value={data.new_municipality_name}
                        onChange={(e) => setData('new_municipality_name', e.target.value)}
                        required
                      />
                      {renderError('new_municipality_name')}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="new_municipality_state">USPS State</Label>
                      <Input
                        id="new_municipality_state"
                        name="new_municipality_state"
                        value={data.new_municipality_state}
                        onChange={(e) => setData('new_municipality_state', e.target.value)}
                        maxLength={2}
                        autoComplete="off"
                        required
                      />
                      {renderError('new_municipality_state')}
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button type="submit">
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Demo Document
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
